import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Check, Loader2, Upload, X, IdCard, Globe, Camera, ImageIcon, Sparkles } from "lucide-react";

/** Convert a File to a compressed JPEG data URL (max 1600px longest edge). */
async function fileToCompressedDataUrl(file, maxSize = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function FileSlot({ id, value, onChange, label, testid }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // On desktop (fine pointer, hover available) both buttons would behave identically
  // because `capture="environment"` is ignored — so we collapse to one "Upload" button.
  // On phones/tablets (coarse pointer, no hover) we show TWO buttons: Camera + Gallery.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse) and (hover: none)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update);
    };
  }, []);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Solo immagini"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
    setBusy(true);
    try {
      const url = await fileToCompressedDataUrl(f);
      onChange(url);
    } catch { toast.error("Errore lettura immagine"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-white/15 hover:border-scooter-yellow/50 transition-colors relative">
      {/* Hidden inputs: one forces the camera (mobile), one opens the gallery/file picker */}
      <input
        ref={cameraInputRef}
        id={`${id}-camera`}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={e => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testid}-camera-input`}
      />
      <input
        ref={galleryInputRef}
        id={`${id}-gallery`}
        type="file"
        accept="image/*"
        onChange={e => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testid}-gallery-input`}
      />

      <div className="p-4 min-h-[9rem] flex flex-col items-center justify-center gap-2 text-center">
        {busy ? (
          <Loader2 className="w-5 h-5 animate-spin text-scooter-yellow" />
        ) : value ? (
          <>
            <img src={value} alt="preview" className="max-h-24 w-auto object-contain" data-testid={`${testid}-preview`} />
            <span className="text-[10px] text-scooter-yellow uppercase tracking-wider font-bold">{label} ✓</span>
            <div className="flex gap-2 mt-1 pointer-events-auto">
              <button
                type="button"
                onClick={() => (isMobile ? cameraInputRef : galleryInputRef).current?.click()}
                className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 hover:text-scooter-yellow flex items-center gap-1"
                data-testid={`${testid}-retake`}
              >
                {isMobile ? <Camera className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
                {isMobile ? (t.license?.retake || "Rifai") : (t.license?.replace || "Cambia")}
              </button>
            </div>
          </>
        ) : isMobile ? (
          // Mobile / tablet: two distinct buttons (camera vs gallery)
          <>
            <div className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-1">{label}</div>
            <div className="flex flex-col sm:flex-row gap-2 w-full pointer-events-auto">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 border border-scooter-yellow/50 bg-scooter-yellow/10 text-scooter-yellow hover:bg-scooter-yellow hover:text-black transition-colors h-11 px-3 text-[11px] tracking-[0.15em] uppercase font-black"
                data-testid={`${testid}-camera-btn`}
              >
                <Camera className="w-4 h-4" strokeWidth={2.4} />
                {t.license?.take_photo || "Scatta foto"}
              </button>
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-zinc-300 hover:text-white transition-colors h-11 px-3 text-[11px] tracking-[0.15em] uppercase font-black"
                data-testid={`${testid}-gallery-btn`}
              >
                <ImageIcon className="w-4 h-4" strokeWidth={2.4} />
                {t.license?.pick_gallery || "Galleria"}
              </button>
            </div>
          </>
        ) : (
          // Desktop: single "Upload" button — camera capture doesn't work here anyway
          <>
            <div className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-1">{label}</div>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              className="flex items-center justify-center gap-2 border border-scooter-yellow/50 bg-scooter-yellow/10 text-scooter-yellow hover:bg-scooter-yellow hover:text-black transition-colors h-11 px-5 text-[11px] tracking-[0.15em] uppercase font-black w-full"
              data-testid={`${testid}-upload-btn`}
            >
              <Upload className="w-4 h-4" strokeWidth={2.4} />
              {t.license?.upload_image || "Carica immagine"}
            </button>
            <span className="text-[10px] text-zinc-500 mt-1">{t.license?.upload_hint_desktop || "JPG · PNG · max 5MB"}</span>
          </>
        )}
      </div>
      {value && !busy && (
        <button
          type="button" onClick={() => onChange(null)}
          className="absolute top-1 right-1 w-6 h-6 bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 text-red-300 flex items-center justify-center z-10"
          data-testid={`${testid}-remove`}
        ><X className="w-3 h-3" /></button>
      )}
    </div>
  );
}

export default function LicenseUploadForm({ bookingId, onDone, submitLabel }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    driver_name: "",
    driver_birthdate: "",
    license_number: "",
    license_expiry: "",
    license_country: "IT",
    is_international: false,
  });
  const [nfr, setNfr] = useState(null);
  const [nbk, setNbk] = useState(null);
  const [ifr, setIfr] = useState(null);
  const [ibk, setIbk] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const extractedFor = useRef(null); // dedupe: don't re-extract the same image

  // When the customer uploads the FRONT of their national license, call Gemini OCR
  // in background to auto-fill the form fields. Only fills EMPTY fields — never
  // overrides manual edits. Runs once per image.
  useEffect(() => {
    if (!nfr || nfr === extractedFor.current) return;
    let cancelled = false;
    extractedFor.current = nfr;
    setExtracting(true);
    (async () => {
      try {
        const { data } = await api.post("/license/extract", { photo: nfr, side: "front" });
        if (cancelled) return;
        if (!data.is_driver_license || !data.readable) {
          // Silent — the customer might correct and take a better photo, but we
          // don't block the flow. The final /license upload will re-verify.
          return;
        }
        setForm(prev => {
          const next = { ...prev };
          const holder = (data.holder_name || `${data.given_names || ""} ${data.family_name || ""}`).trim();
          if (!next.driver_name && holder) next.driver_name = holder;
          if (!next.driver_birthdate && data.birthdate) next.driver_birthdate = data.birthdate;
          if (!next.license_number && data.license_number) next.license_number = data.license_number;
          if (!next.license_expiry && data.expiry_date) next.license_expiry = data.expiry_date;
          if ((!next.license_country || next.license_country === "IT") && data.issuing_country) {
            next.license_country = data.issuing_country.toUpperCase().slice(0, 2) || next.license_country;
          }
          return next;
        });
        setAutofilled(true);
        toast.success(t.license?.autofilled || "Dati estratti dalla patente ✓ verifica e correggi se serve");
      } catch (e) {
        // Silent fail — customer can still fill manually
        if (e.response?.status !== 422) {
          // Only warn on server errors, not on unreadable images
          console.warn("license extract failed:", e.response?.data?.detail || e.message);
        }
      } finally {
        if (!cancelled) setExtracting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nfr, t]);

  const submit = async () => {
    if (!form.driver_name || !form.driver_birthdate || !form.license_number || !form.license_expiry || !form.license_country) {
      return toast.error(t.license.fill_required);
    }
    if (!nfr) return toast.error(t.license.upload_national);
    if (!nbk) return toast.error(t.license.upload_national_back || "Foto retro patente obbligatoria");
    if (form.is_international && !ifr) return toast.error(t.license.upload_international);
    if (form.is_international && !ibk) return toast.error(t.license.upload_international_back || "Foto retro patente internazionale obbligatoria");
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/license`, {
        ...form,
        national_front: nfr,
        national_back: nbk,
        international_front: form.is_international ? ifr : null,
        international_back: form.is_international ? ibk : null,
      });
      toast.success(t.license.saved);
      onDone && onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 422 = AI verification failure — show as toast with longer duration + reset button state
      if (e.response?.status === 422) {
        toast.error(detail || "Verifica patente fallita", { duration: 8000 });
      } else {
        toast.error(detail || "Errore");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-scooter-yellow/40 bg-scooter-yellow/[0.03] p-6 sm:p-8" data-testid="license-form">
      <div className="flex items-center gap-2 mb-2">
        <IdCard className="w-5 h-5 text-scooter-yellow" />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter">{t.license.title}</h2>
      </div>
      <p className="text-zinc-400 text-sm mb-3">{t.license.subtitle}</p>
      <div className="text-[11px] text-scooter-yellow/80 mb-6 border-l-2 border-scooter-yellow/40 pl-3 py-1" data-testid="license-verify-hint">
        {t.license.autofill_hint || "Scatta o carica prima la foto della patente: compileremo i campi automaticamente."}
      </div>

      {/* International toggle FIRST — determines which photos to show */}
      <div className="border border-white/10 p-4 mb-6" data-testid="intl-toggle-section">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-scooter-yellow" />
            <span className="text-sm font-bold text-white">{t.license.is_intl_q}</span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={form.is_international} onChange={e => setForm(f => ({...f, is_international: e.target.checked}))} className="sr-only peer" data-testid="license-intl-toggle" />
            <div className="w-11 h-6 bg-white/10 peer-checked:bg-scooter-yellow rounded-full transition-colors">
              <div className={`h-5 w-5 bg-black rounded-full mt-0.5 transition-transform ${form.is_international ? 'translate-x-[22px]' : 'translate-x-0.5'}`}></div>
            </div>
          </label>
        </div>
        <p className="text-[11px] text-zinc-500">{form.is_international ? t.license.intl_yes_hint : t.license.intl_no_hint}</p>
      </div>

      {/* Photos FIRST — the front photo auto-fills the fields below */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.license.national_photos}</div>
          {extracting && (
            <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-scooter-yellow" data-testid="license-extracting">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t.license?.extracting || "Estrazione dati…"}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FileSlot id="nfr" value={nfr} onChange={setNfr} label={t.license.front_required || `${t.license.front} *`} testid="license-national-front" />
          <FileSlot id="nbk" value={nbk} onChange={setNbk} label={t.license.back_required || `${t.license.back} *`} testid="license-national-back" />
        </div>
      </div>

      {form.is_international && (
        <div className="mb-6" data-testid="license-intl-photos">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow mb-3">{t.license.intl_photos}</div>
          <div className="grid grid-cols-2 gap-3">
            <FileSlot id="ifr" value={ifr} onChange={setIfr} label={t.license.front_required || `${t.license.front} *`} testid="license-intl-front" />
            <FileSlot id="ibk" value={ibk} onChange={setIbk} label={t.license.back_required || `${t.license.back} *`} testid="license-intl-back" />
          </div>
        </div>
      )}

      {/* Fields NOW — pre-filled if a photo was uploaded */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.license?.data_label || "Dati patente"}</div>
          {autofilled && (
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-bold text-scooter-yellow border border-scooter-yellow/50 px-2 py-1" data-testid="license-autofilled-badge">
              <Sparkles className="w-3 h-3" strokeWidth={2.4} />
              {t.license?.autofilled_badge || "Compilato dalla foto"}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.license.driver_name}</Label>
          <Input value={form.driver_name} onChange={e => setForm(f => ({...f, driver_name: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" data-testid="license-name" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.license.birthdate}</Label>
          <Input type="date" value={form.driver_birthdate} onChange={e => setForm(f => ({...f, driver_birthdate: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" data-testid="license-birthdate" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.license.number}</Label>
          <Input value={form.license_number} onChange={e => setForm(f => ({...f, license_number: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" data-testid="license-number" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.license.expiry}</Label>
          <Input type="date" value={form.license_expiry} onChange={e => setForm(f => ({...f, license_expiry: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" data-testid="license-expiry" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.license.country}</Label>
          <Input value={form.license_country} onChange={e => setForm(f => ({...f, license_country: e.target.value.toUpperCase()}))} maxLength={40} placeholder="IT" className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" data-testid="license-country" />
        </div>
      </div>

      <button onClick={submit} disabled={saving} className="w-full h-14 bg-scooter-yellow text-black font-bold text-sm tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center justify-center gap-2" data-testid="license-submit">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
        {submitLabel || t.license.submit}
      </button>
    </div>
  );
}
